import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { DesignVals } from '@/lib/vals/design';

export function DesignPage({ v }: { v: DesignVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="Design system"
        title="One file is allowed to contain a hex literal."
        highlight={['hex', 'literal.']}
        lede="Tokens come in three tiers: raw ramps, semantic names remapped per theme, and component values that reference only the semantic tier. Components touch the top two tiers and never a raw colour."
        aside={
          <div className="theme-switch">
            <span className="eyebrow">Preview</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={v.setTerminal}>
              Terminal
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={v.setCanon}>
              Canon
            </button>
          </div>
        }
      />

      {/* Swatches */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Semantic tier
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Sixteen names the whole product speaks."
            highlight={['names']}
          />
          <div className="swatches">
            {v.swatches.map((s, i) => (
              <Reveal key={s.name} delay={Math.min(i, 10) * 40}>
                <div className="swatch glass">
                  <span className="swatch__chip" style={{ background: s.value }} />
                  <code>{s.name}</code>
                  <span className="swatch__use">{s.use}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Type scale */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Type
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Six sizes, and what each is for."
            highlight={['Six', 'sizes,']}
          />
          <div className="scale">
            {v.scale.map((t, i) => (
              <Reveal key={t.token} delay={i * 70}>
                <div className="scale__row glass">
                  <span className="scale__sample" style={{ fontSize: t.size }}>
                    Aa
                  </span>
                  <div>
                    <code>{t.token}</code>
                    <span className="scale__size">{t.size}</span>
                  </div>
                  <p>{t.use}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ink rules */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Direction is earned, attention is not approval
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="A number only takes colour when it moved against something."
            highlight={['moved']}
          />
          <div className="inkrules">
            {v.inkRules.map((r, i) => (
              <Reveal key={r.label} delay={i * 70}>
                <div className="inkrule glass">
                  <span className="inkrule__sample" style={{ color: r.color }}>
                    {r.sample}
                  </span>
                  <span className="inkrule__label">{r.label}</span>
                  <p>{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Densities */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Density, not zoom
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Three densities. Relaxed is the touch profile."
            highlight={['touch', 'profile.']}
          />
          <div className="densities">
            {v.densities.map((d, i) => (
              <Reveal key={d.name} delay={i * 90}>
                <TiltCard className="density glass" max={3}>
                  <div className="density__head">
                    <h3>{d.name}</h3>
                    <code>{d.meta}</code>
                  </div>
                  <div className="density__rows" aria-hidden="true">
                    {d.rows.map((r, j) => (
                      <span key={j} style={{ height: r.h }} />
                    ))}
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Notes */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Decisions recorded, not assumed
          </Reveal>
          <div className="clusters">
            {v.notes.map((n, i) => (
              <Reveal key={n.title} delay={i * 70}>
                <TiltCard className="cluster glass" max={3}>
                  <div className="cluster__head">
                    <h3>{n.title}</h3>
                  </div>
                  <p className="cluster__blurb">{n.body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
